import type {
  AttachmentEntityType,
  EntityAttachment,
} from '@internal/orquestra-contract';
import { OrquestraClient } from './OrquestraApi';
import type { AttachmentUploadProgress } from './OrquestraApi.types';
import { entityAttachmentsPath } from './attachmentUpload';

/**
 * Estende `OrquestraClient` com os anexos de Processo AS IS, Processo TO BE e
 * Solução Imagine IA (`/projects/:id/attachments/:entityType/:entityId`).
 * Classe intermediária da cadeia de clients.
 */
export class OrquestraEntityAttachmentsClient extends OrquestraClient {
  /** Lista os anexos de uma entidade (Processo AS IS/TO BE, Solução Imagine). */
  async listEntityAttachments(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
  ): Promise<EntityAttachment[]> {
    return this.request<EntityAttachment[]>(
      entityAttachmentsPath(projectId, entityType, entityId),
    );
  }

  /** Upload com progresso de um anexo da entidade (mesmos erros 413/400). */
  async uploadEntityAttachment(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
    file: File,
    onProgress?: (progress: AttachmentUploadProgress) => void,
  ): Promise<EntityAttachment> {
    return this.uploadFile<EntityAttachment>(
      entityAttachmentsPath(projectId, entityType, entityId),
      file,
      onProgress,
      `Falha de rede ao enviar anexo ${file.name}.`,
    );
  }

  /** Remove um anexo da entidade. */
  async deleteEntityAttachment(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
    attachmentId: number,
  ): Promise<void> {
    await this.deleteFile(
      `${entityAttachmentsPath(
        projectId,
        entityType,
        entityId,
      )}/${attachmentId}`,
    );
  }

  /** URL absoluta do conteúdo do anexo da entidade (`inline` para preview). */
  async getEntityAttachmentDownloadUrl(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
    attachmentId: number,
    disposition?: 'inline' | 'attachment',
  ): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const path = `${baseUrl}${entityAttachmentsPath(
      projectId,
      entityType,
      entityId,
    )}/${attachmentId}/content`;
    return disposition ? `${path}?disposition=${disposition}` : path;
  }
}
